import React, { useEffect, useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Link as ScrollLink } from 'react-scroll';
import anime from 'animejs';
import {
    FiFlag, FiServer, FiMonitor, FiVideo, FiShield, FiAward,
    FiCpu, FiTerminal, FiArrowRight, FiArrowUpRight, FiCalendar, FiMapPin,
    FiZap, FiTarget, FiCheckCircle,
} from 'react-icons/fi';
import { FaWhatsapp } from 'react-icons/fa6';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import ShapeGrid from '../components/fx/ShapeGrid';
import FloatingShapes from '../components/fx/FloatingShapes';
import SplitText from '../components/fx/SplitText';
import Magnetic from '../components/fx/Magnetic';
import CascadeGrid from '../components/fx/CascadeGrid';
import ClickBurst from '../components/fx/ClickBurst';
import Reveal from '../components/fx/Reveal';

const capabilities = [
    {
        icon: <FiTarget />,
        title: 'Custom Problem Design',
        desc: 'Original challenges across web, pwn, reverse, crypto, forensics, OSINT, blockchain, mobile, and cloud — calibrated to your audience.',
    },
    {
        icon: <FiServer />,
        title: 'Battle-Tested Infrastructure',
        desc: 'CTFd / rCTF-style scoreboards, per-team isolated dynamic challenges, attack-defense gateways, and SiberBox VPN access.',
    },
    {
        icon: <FiMonitor />,
        title: 'Live Operations Crew',
        desc: 'On-site marshalling, real-time challenge support, and ticket-triage so participants stay in flow and out of frustration.',
    },
    {
        icon: <FiVideo />,
        title: 'Production & Livestream',
        desc: 'Opening/closing ceremonies, on-air color commentary, highlight reels, and post-event write-ups.',
    },
    {
        icon: <FiAward />,
        title: 'Judging & Prizing',
        desc: 'Fair scoring, anti-cheat enforcement, transparent disputes process, and prize procurement & distribution.',
    },
    {
        icon: <FiShield />,
        title: 'Security & Anti-Cheat',
        desc: 'Flag tracking, share detection, IP fingerprinting, traffic anomaly heuristics — published in an open post-mortem.',
    },
];

const formats = [
    { name: 'Jeopardy', desc: 'Classic categorized challenges. Solve to score.', icon: <FiFlag />, color: 'from-red-500 to-rose-700' },
    { name: 'Attack & Defense', desc: 'Live boxes. Steal flags, patch yours. Tick-based scoring.', icon: <FiZap />, color: 'from-amber-500 to-red-600' },
    { name: 'King of the Hill', desc: 'One target. Hold root. Score-per-tick.', icon: <FiTerminal />, color: 'from-purple-500 to-red-600' },
    { name: 'Boot2Root Marathon', desc: 'Linked challenge chains across a fictional company.', icon: <FiCpu />, color: 'from-cyan-500 to-red-600' },
];

const portfolio = [
    {
        title: 'Wreck IT 7.0 Capture The Flag',
        date: '2026',
        scope: 'National',
        organizer: 'Politeknik Siber dan Sandi Negara',
        notes: 'Seventh edition of the Wreck IT CTF series.',
        categories: ['web', 'pwn', 'crypto', 'rev', 'forensics'],
    },
    {
        title: 'LKS Keamanan Siber, Jakarta Timur Region 2',
        date: '2026',
        scope: 'Regional',
        organizer: 'Kementerian Pendidikan Dasar dan Menengah RI',
        notes: 'Lomba Kompetensi Siswa cabang Keamanan Siber, region Jakarta Timur 2.',
        categories: ['lks', 'student', 'regional'],
    },
    {
        title: 'Gemastik 2025',
        date: '2025',
        scope: 'National',
        organizer: 'Telkom University',
        notes: 'Pagelaran Mahasiswa Nasional Bidang TIK, cabang Keamanan Siber.',
        categories: ['ctf', 'national'],
    },
    {
        title: 'Rise The Ranger SATSIBER TNI 2025',
        date: '2025',
        scope: 'Military / National',
        organizer: 'SATSIBER TNI',
        notes: 'Cyber competition with Satuan Siber TNI.',
        categories: ['ctf', 'tni', 'national'],
    },
    {
        title: 'CNCC 2025, Cambodia National Cyber Competition',
        date: '2025',
        scope: 'International',
        organizer: 'Ministry of Post and Telecommunications, Cambodia',
        notes: 'Cambodia National Cyber Competition.',
        categories: ['ctf', 'international'],
    },
    {
        title: 'Wreck IT 6.0 Capture The Flag',
        date: '2025',
        scope: 'National',
        organizer: 'Politeknik Siber dan Sandi Negara',
        notes: 'Sixth edition of the Wreck IT CTF series.',
        categories: ['web', 'pwn', 'crypto', 'rev', 'forensics'],
    },
    {
        title: 'LKS Keamanan Siber, Jakarta Timur Region 2',
        date: '2025',
        scope: 'Regional',
        organizer: 'Kementerian Pendidikan Dasar dan Menengah RI',
        notes: 'Lomba Kompetensi Siswa cabang Keamanan Siber, region Jakarta Timur 2.',
        categories: ['lks', 'student', 'regional'],
    },
    {
        title: 'Wreck IT 5.0 Capture The Flag',
        date: '2024',
        scope: 'National',
        organizer: 'Politeknik Siber dan Sandi Negara',
        notes: 'Fifth edition of the Wreck IT CTF series.',
        categories: ['web', 'pwn', 'crypto', 'rev', 'forensics'],
    },
    {
        title: 'FINDIT UGM 2023',
        date: '2023',
        scope: 'National',
        organizer: 'Universitas Gadjah Mada',
        notes: 'FINDIT 2023, national-scale event.',
        categories: ['ctf', 'national'],
    },
];

const problemSetByCategory = [
    { cat: 'Web', count: 62, sample: ['JWT confusion in microservice mesh', 'SSRF → AWS metadata → assume-role', 'Prototype pollution to RCE on Node SSR', 'Race condition on coupon redemption', 'OAuth redirect chain ATO'] },
    { cat: 'Pwn / Binary', count: 38, sample: ['Heap feng-shui on glibc 2.35', 'Custom kernel module priv-esc', 'ARM64 ROP under PIE+canary', 'Format-string in IoT firmware', 'V8 jitspray (demo only)'] },
    { cat: 'Reverse', count: 34, sample: ['VM-protected license validator', 'Custom obfuscator unmasking', 'WASM-based crackme', 'Anti-debug Android APK', 'ELF packer reversing'] },
    { cat: 'Crypto', count: 28, sample: ['Bleichenbacher on legacy bank API', 'LCG state recovery', 'AES-CBC padding oracle', 'ECDSA nonce reuse', 'PRNG misuse in JWT signing'] },
    { cat: 'Forensics / DFIR', count: 26, sample: ['Memory dump triage (volatility)', 'Wireshark beacon hunt', 'Email header forensics', 'NTFS USN journal reconstruction', 'Cloud-trail log archaeology'] },
    { cat: 'OSINT', count: 18, sample: ['Geo-trace from EXIF + skyline', 'Pivot from leaked GitHub gist', 'Reverse image + corporate filings', 'Social engineering narrative chain', 'Domain WHOIS pivot'] },
    { cat: 'Mobile', count: 14, sample: ['Android intent redirection', 'iOS plist secret leakage', 'Frida bypass on root detection', 'Insecure deep links', 'WebView XSS'] },
    { cat: 'Cloud', count: 12, sample: ['Misconfigured S3 + signed URL', 'GKE workload identity abuse', 'Terraform state secret exposure', 'IAM assume-role chain', 'Lambda env leak via SSRF'] },
    { cat: 'Blockchain', count: 8, sample: ['Reentrancy on Solidity vault', 'Flash-loan oracle manipulation', 'Signature malleability', 'Front-running mempool', 'Proxy slot collision'] },
];

const timelineSteps = [
    { phase: 'T-8w', title: 'Scoping', desc: 'Goals, audience, format, budget, success metrics.' },
    { phase: 'T-6w', title: 'Problem Design', desc: 'Authoring & cross-review of challenge set with difficulty curve.' },
    { phase: 'T-4w', title: 'Infra Build-out', desc: 'Scoreboard, per-team isolation, attacker VPN, monitoring.' },
    { phase: 'T-2w', title: 'Dry Run', desc: 'Internal play-through, balance pass, bug squashing.' },
    { phase: 'T-1w', title: 'Comms & Onboarding', desc: 'Participant briefing, rule book, comms channels.' },
    { phase: 'T-0', title: 'Event Day', desc: 'Ops crew on standby, livestream rolling, ticket triage live.' },
    { phase: 'T+1w', title: 'Post-mortem', desc: 'Writeups published, scoreboard archived, transparency report.' },
];

const CTFVendorPage = () => {
    const heroRef = useRef(null);
    const heroTitleRef = useRef(null);
    const heroCtaRef = useRef(null);
    const heroStatsRef = useRef(null);
    const [activeCat, setActiveCat] = useState(0);
    useEffect(() => {
        const tl = anime.timeline({ easing: 'easeOutCubic' });
        if (heroTitleRef.current) {
            tl.add({
                targets: heroTitleRef.current.querySelectorAll('.char'),
                opacity: [0, 1],
                translateY: [-40, 0],
                rotateZ: [-15, 0],
                scale: [0.7, 1],
                duration: 700,
                delay: anime.stagger(18, { from: 'first' }),
            }, 0);
        }
        if (heroCtaRef.current) {
            tl.add({
                targets: heroCtaRef.current.querySelectorAll('.cta-item'),
                opacity: [0, 1],
                translateY: [20, 0],
                scale: [0.92, 1],
                duration: 700,
                delay: anime.stagger(90),
                easing: 'easeOutBack',
            }, '-=400');
        }
        if (heroStatsRef.current) {
            tl.add({
                targets: heroStatsRef.current.querySelectorAll('.stat'),
                opacity: [0, 1],
                translateY: [30, 0],
                rotateX: [-25, 0],
                duration: 800,
                delay: anime.stagger(110),
            }, '-=400');
        }
    }, []);

    return (
        <>
            <Navbar />

            {/* HERO */}
            <section ref={heroRef} className="relative bg-[#05060a] text-white overflow-hidden" style={{ perspective: '1200px' }}>
                <div className="absolute inset-0 bg-grid opacity-40" />
                <div className="absolute inset-0 spotlight" />
                <ShapeGrid density={42} />
                <FloatingShapes />
                <ClickBurst targetRef={heroRef} />
                <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-b from-transparent to-[#05060a] z-10" />

                <div className="relative z-20 px-6 md:px-16 pt-24 pb-20 max-w-7xl mx-auto">
                    <div className="flex items-center gap-3 mb-8">
                        <span className="pill tag"><span className="pulse-dot" /> Now booking</span>
                        <span className="hidden md:inline text-xs text-gray-400 font-mono">capture_the_flag.ops</span>
                    </div>

                    <h1 ref={heroTitleRef} className="font-display text-5xl md:text-7xl lg:text-[5.4rem] font-bold leading-[1.02] mb-6 max-w-5xl">
                        <span className="block"><SplitText text="We run" /></span>
                        <span className="block text-gradient-red"><SplitText text="Capture The Flag" /></span>
                        <span className="block"><SplitText text="end-to-end." /></span>
                    </h1>
                    <p className="text-gray-300 max-w-3xl mb-8 text-lg leading-relaxed">
                        From original challenge design to attack-defense infrastructure, on-site marshalling,
                        and livestream production. SiberLab has run national-scale CTFs, corporate red-team
                        days, and university qualifiers. We bring the same crew, scoreboard, and uptime SLA
                        to your event.
                    </p>
                    <div ref={heroCtaRef} className="flex flex-wrap gap-3 mb-14">
                        <ScrollLink to="contact" smooth duration={600} offset={-40} className="cta-item">
                            <Magnetic>
                                <span className="btn-primary cursor-pointer">Plan your CTF <FiArrowRight /></span>
                            </Magnetic>
                        </ScrollLink>
                        <ScrollLink to="portfolio" smooth duration={600} offset={-40} className="cta-item">
                            <Magnetic>
                                <span className="btn-ghost cursor-pointer">See portfolio</span>
                            </Magnetic>
                        </ScrollLink>
                        <a href="https://wa.me/6281234868640?text=Hi%20SiberLab%2C%20I%27d%20like%20to%20discuss%20a%20CTF%20event."
                            target="_blank" rel="noreferrer" className="cta-item">
                            <Magnetic>
                                <span className="btn-ghost"><FaWhatsapp /> WhatsApp ops</span>
                            </Magnetic>
                        </a>
                    </div>

                </div>
            </section>

            {/* CAPABILITIES */}
            <section className="relative bg-[#08090f] text-white px-6 md:px-16 py-24 overflow-hidden">
                <div className="absolute inset-0 bg-dots opacity-20" />
                <div className="relative max-w-7xl mx-auto">
                    <Reveal className="text-center mb-14">
                        <span className="pill tag inline-flex mb-4">CAPABILITIES</span>
                        <h2 className="font-display text-4xl md:text-5xl font-bold">
                            Everything an event needs. <span className="text-gradient-red">One vendor.</span>
                        </h2>
                    </Reveal>
                    <CascadeGrid className="grid gap-6 md:grid-cols-2 lg:grid-cols-3" stagger={100}>
                        {capabilities.map((c, i) => (
                            <div key={i} className="card p-6 h-full">
                                <div className="w-12 h-12 rounded-xl bg-red-500/10 border border-red-500/30 flex items-center justify-center text-red-400 mb-4">
                                    {c.icon}
                                </div>
                                <h3 className="font-display text-lg font-semibold mb-2">{c.title}</h3>
                                <p className="text-gray-400 leading-relaxed text-sm">{c.desc}</p>
                            </div>
                        ))}
                    </CascadeGrid>
                </div>
            </section>

            {/* FORMATS */}
            <section className="relative bg-[#05060a] text-white px-6 md:px-16 py-24 overflow-hidden">
                <div className="relative max-w-7xl mx-auto">
                    <Reveal className="mb-12">
                        <span className="pill tag inline-flex mb-4">FORMATS</span>
                        <h2 className="font-display text-4xl md:text-5xl font-bold">
                            Pick your <span className="text-gradient-red">format</span>.
                        </h2>
                        <p className="text-gray-400 mt-3 max-w-2xl text-lg">
                            We've run every flavor of CTF. Hybrid formats welcome.
                        </p>
                    </Reveal>
                    <CascadeGrid className="grid gap-5 md:grid-cols-2 lg:grid-cols-4" stagger={110}>
                        {formats.map((f, i) => (
                            <div key={i} className="card p-6 h-full relative overflow-hidden">
                                <div className={`absolute -top-10 -right-10 w-40 h-40 rounded-full bg-gradient-to-br ${f.color} opacity-20 blur-2xl`} />
                                <div className="relative">
                                    <div className="text-red-400 mb-3">{f.icon}</div>
                                    <h3 className="font-display text-xl font-semibold mb-2">{f.name}</h3>
                                    <p className="text-gray-400 text-sm">{f.desc}</p>
                                </div>
                            </div>
                        ))}
                    </CascadeGrid>
                </div>
            </section>

            {/* PROBLEM SETS */}
            <section className="relative bg-[#08090f] text-white px-6 md:px-16 py-24 overflow-hidden">
                <div className="absolute inset-0 bg-grid opacity-40" />
                <div className="relative max-w-7xl mx-auto">
                    <Reveal className="mb-12">
                        <span className="pill tag inline-flex mb-4">PROBLEM SET</span>
                        <h2 className="font-display text-4xl md:text-5xl font-bold">
                            240+ original <span className="text-gradient-red">challenges</span> in our vault.
                        </h2>
                        <p className="text-gray-400 mt-3 max-w-2xl text-lg">
                            Every problem we author is original, peer-reviewed, and tuned to an explicit
                            difficulty bracket. Browse by category.
                        </p>
                    </Reveal>

                    <div className="grid lg:grid-cols-12 gap-6">
                        {/* Category list */}
                        <Reveal className="lg:col-span-4">
                            <div className="card p-3 h-full">
                                <ul>
                                    {problemSetByCategory.map((p, i) => (
                                        <li key={i}>
                                            <button
                                                onClick={() => setActiveCat(i)}
                                                className={`w-full flex items-center justify-between px-4 py-3 rounded-lg transition ${
                                                    activeCat === i
                                                        ? 'bg-red-500/10 border border-red-500/30'
                                                        : 'border border-transparent hover:bg-white/5'
                                                }`}
                                            >
                                                <span className="font-display text-base">{p.cat}</span>
                                                <span className="font-mono text-xs text-gray-400">{p.count}</span>
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </Reveal>

                        {/* Sample challenges */}
                        <Reveal className="lg:col-span-8">
                            <div className="card p-6 h-full">
                                <div className="flex items-center justify-between mb-5">
                                    <h3 className="font-display text-2xl font-semibold">
                                        {problemSetByCategory[activeCat].cat}
                                    </h3>
                                    <span className="pill">
                                        {problemSetByCategory[activeCat].count} challenges
                                    </span>
                                </div>
                                <ul className="space-y-3">
                                    {problemSetByCategory[activeCat].sample.map((s, j) => (
                                        <li key={j} className="flex items-start gap-3 p-3 rounded-lg bg-black/30 border border-white/5">
                                            <FiCheckCircle className="text-red-400 mt-1 flex-shrink-0" />
                                            <span className="text-gray-200">{s}</span>
                                        </li>
                                    ))}
                                </ul>
                                <p className="text-xs text-gray-500 mt-4 font-mono">
                                    Full vault under NDA. Sample shown for illustration.
                                </p>
                            </div>
                        </Reveal>
                    </div>

                </div>
            </section>

            {/* PORTFOLIO */}
            <section id="portfolio" className="relative bg-[#05060a] text-white px-6 md:px-16 py-24 overflow-hidden">
                <div className="absolute inset-0 bg-dots opacity-20" />
                <div className="relative max-w-7xl mx-auto">
                    <Reveal className="mb-12">
                        <span className="pill tag inline-flex mb-4">PORTFOLIO</span>
                        <h2 className="font-display text-4xl md:text-5xl font-bold">
                            Events we've run.
                        </h2>
                        <p className="text-gray-400 mt-3 max-w-2xl text-lg">
                            A timeline of CTF events SiberLab has been involved in, from regional student competitions to national qualifiers and international invitationals.
                        </p>
                    </Reveal>

                    <div className="space-y-4">
                        {portfolio.map((ev, i) => (
                            <Reveal key={i} delay={i * 50}>
                                <div className="card p-6 grid md:grid-cols-12 gap-5">
                                    <div className="md:col-span-3 flex flex-col gap-2">
                                        {ev.date && (
                                            <div className="flex items-center gap-2 text-red-400 text-sm font-mono">
                                                <FiCalendar /> {ev.date}
                                            </div>
                                        )}
                                        <div className="flex items-center gap-2 text-gray-400 text-sm">
                                            <FiMapPin /> {ev.scope}
                                        </div>
                                    </div>
                                    <div className="md:col-span-9">
                                        <h3 className="font-display text-xl font-semibold mb-1">{ev.title}</h3>
                                        {ev.organizer && (
                                            <p className="text-xs font-mono uppercase tracking-wider text-red-300/80 mb-2">
                                                Organized by {ev.organizer}
                                            </p>
                                        )}
                                        <p className="text-gray-400 text-sm mb-3">{ev.notes}</p>
                                        <div className="flex flex-wrap gap-2">
                                            {ev.categories.map((c, j) => (
                                                <span key={j} className="text-xs font-mono px-2 py-1 rounded bg-white/5 border border-white/10 text-gray-300">
                                                    {c}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* TIMELINE */}
            <section className="relative bg-[#05060a] text-white px-6 md:px-16 py-24 overflow-hidden">
                <div className="relative max-w-7xl mx-auto">
                    <Reveal className="mb-12">
                        <span className="pill tag inline-flex mb-4">PROCESS</span>
                        <h2 className="font-display text-4xl md:text-5xl font-bold">
                            From kickoff to <span className="text-gradient-red">post-mortem</span>.
                        </h2>
                        <p className="text-gray-400 mt-3 max-w-2xl text-lg">
                            A typical 8-week event timeline. We've compressed to 3 weeks before. Talk to us.
                        </p>
                    </Reveal>
                    <div className="relative pl-6 md:pl-0">
                        <div className="absolute left-2 md:left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-red-500/50 to-transparent" />
                        {timelineSteps.map((s, i) => (
                            <Reveal key={i} delay={i * 80}>
                                <div className={`relative mb-8 md:flex md:items-center md:gap-8 ${i % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                                    <div className="md:w-1/2">
                                        <div className="card p-5">
                                            <span className="text-xs font-mono text-red-400 uppercase tracking-widest">{s.phase}</span>
                                            <h4 className="font-display text-xl font-semibold mt-1 mb-2">{s.title}</h4>
                                            <p className="text-gray-400 text-sm">{s.desc}</p>
                                        </div>
                                    </div>
                                    <div className="absolute left-2 md:left-1/2 -translate-x-1/2 w-4 h-4 rounded-full bg-red-500 ring-4 ring-red-500/20 -mt-2 md:mt-0" />
                                    <div className="md:w-1/2" />
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section id="contact" className="relative bg-[#08090f] text-white px-6 md:px-16 py-24 overflow-hidden">
                <ShapeGrid density={48} interactive={false} />
                <FloatingShapes />
                <div className="absolute inset-0 spotlight" />
                <Reveal className="relative max-w-3xl mx-auto text-center">
                    <span className="pill tag inline-flex mb-5">LET'S RUN ONE</span>
                    <h2 className="font-display text-4xl md:text-6xl font-bold mb-5 leading-tight">
                        Plan your <span className="text-gradient-red">CTF</span> with SiberLab.
                    </h2>
                    <p className="text-gray-300 mb-8 text-lg">
                        Drop us a line. We respond within one business day with a tailored proposal,
                        timeline, and budget range.
                    </p>
                    <div className="flex flex-wrap gap-3 justify-center">
                        <a href="https://wa.me/6281234868640?text=Hi%20SiberLab%2C%20I%27d%20like%20to%20discuss%20a%20CTF%20event."
                            target="_blank" rel="noreferrer" className="btn-primary">
                            <FaWhatsapp /> WhatsApp ops
                        </a>
                        <a href="mailto:admin@siberlab.id?subject=CTF%20Event%20Inquiry" className="btn-ghost">
                            admin@siberlab.id <FiArrowUpRight />
                        </a>
                        <RouterLink to="/" className="btn-ghost">Back home</RouterLink>
                    </div>
                </Reveal>
            </section>

            <Footer />
        </>
    );
};

export default CTFVendorPage;
